import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";

const VOLTS = ["35", "110", "220", "500"];
const VOLT_ORDER_DESC = ["500", "220", "110", "35"];
const CITY_ORDER = [
  "qoraqalpogiston",
  "andijon",
  "buxoro",
  "jizzax",
  "qashqadaryo",
  "navoiy",
  "namangan",
  "samarqand",
  "surxandaryo",
  "sirdaryo",
  "toshkent",
  "fargona",
  "horazm",
  "toshkent-shahar",
];
const CITY_ORDER_MAP = new Map(CITY_ORDER.map((s, i) => [s, i]));

const getCityIdFromObj = (obj) =>
  obj?.cityId ?? obj?.city_id ?? obj?.city?.id ?? null;

const pickPrimaryVoltage = (raw) => {
  const s = String(raw ?? "");
  for (const v of VOLT_ORDER_DESC) if (s.includes(v)) return v;
  return null;
};

const toNumber = (v) => {
  if (v == null) return 0;
  const n = Number(String(v).replace(",", ".").replace(/\s/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const formatInt = (n) => String(n ?? 0);

const formatKm = (n) => {
  const x = Number(n ?? 0);
  if (!Number.isFinite(x)) return "0";
  return x % 1 === 0 ? String(x.toFixed(0)) : String(x.toFixed(1));
};

const Indicators = () => {
  const [city, setCity] = useState([]);
  const [substations, setSubstations] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [summaryMode, setSummaryMode] = useState("substations");
  const [loadingState, setLoadingState] = useState({
    city: true,
    substations: true,
    networks: true,
  });
  const [errorState, setErrorState] = useState({
    city: "",
    substations: "",
    networks: "",
  });

  useEffect(() => {
    async function fetchCity() {
      setLoadingState((prev) => ({ ...prev, city: true }));
      setErrorState((prev) => ({ ...prev, city: "" }));

      try {
        const response = await apiFetch("/api/city/", { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setCity(data);
      } catch (error) {
        console.error("Error fetching cities:", error);
        setErrorState((prev) => ({
          ...prev,
          city: "Филиаллар рўйхатини юклашда хато.",
        }));
      } finally {
        setLoadingState((prev) => ({ ...prev, city: false }));
      }
    }

    fetchCity();
  }, []);

  useEffect(() => {
    async function fetchSubstations() {
      setLoadingState((prev) => ({ ...prev, substations: true }));
      setErrorState((prev) => ({ ...prev, substations: "" }));

      try {
        const response = await apiFetch("/api/substation/", { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setSubstations(data);
      } catch (error) {
        console.error("Error fetching substations:", error);
        setErrorState((prev) => ({
          ...prev,
          substations: "Подстанциялар маълумотини юклашда хато.",
        }));
      } finally {
        setLoadingState((prev) => ({ ...prev, substations: false }));
      }
    }

    fetchSubstations();
  }, []);

  useEffect(() => {
    async function fetchNetworks() {
      setLoadingState((prev) => ({ ...prev, networks: true }));
      setErrorState((prev) => ({ ...prev, networks: "" }));

      try {
        const response = await apiFetch("/api/network/", { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setNetworks(data);
      } catch (error) {
        console.error("Error fetching networks:", error);
        setErrorState((prev) => ({
          ...prev,
          networks: "ЭУХТ маълумотини юклашда хато.",
        }));
      } finally {
        setLoadingState((prev) => ({ ...prev, networks: false }));
      }
    }

    fetchNetworks();
  }, []);

  const SUMMARY_COLS = useMemo(
    () => ({
      substations: [
        { key: "subsTotal", label: "Умумий" },
        { key: "35", label: "35" },
        { key: "110", label: "110" },
        { key: "220", label: "220" },
        { key: "500", label: "500" },
      ],
      networks: [
        { key: "netLenTotal", label: "Умумий км." },
        { key: "35", label: "35" },
        { key: "110", label: "110" },
        { key: "220", label: "220" },
        { key: "500", label: "500" },
      ],
    }),
    []
  );

  const renderSummaryCell = (row, mode, colKey) => {
    if (mode === "substations") {
      if (colKey === "subsTotal") return formatInt(row.subsTotal);
      return formatInt(row.subsByV[colKey] ?? 0);
    }

    if (colKey === "netLenTotal") return formatKm(row.netLenTotal);
    return formatKm(row.netLenByV[colKey] ?? 0);
  };

  const summaryRows = useMemo(() => {
    const base = {};
    for (const v of VOLTS) base[v] = 0;

    const statsByCity = new Map();
    for (const c of city || []) {
      statsByCity.set(String(c.id), {
        city: c,
        subsTotal: 0,
        subsByV: { ...base },
        netLenTotal: 0,
        netLenByV: { ...base },
      });
    }

    for (const s of substations || []) {
      const cityId = getCityIdFromObj(s);
      if (cityId == null) continue;
      const st = statsByCity.get(String(cityId));
      if (!st) continue;

      st.subsTotal += 1;
      const pv = pickPrimaryVoltage(s?.voltage);
      if (pv && st.subsByV[pv] != null) st.subsByV[pv] += 1;
    }

    for (const n of networks || []) {
      const cityId = getCityIdFromObj(n);
      if (cityId == null) continue;
      const st = statsByCity.get(String(cityId));
      if (!st) continue;

      const len = toNumber(n?.length);
      st.netLenTotal += len;

      const pv = pickPrimaryVoltage(n?.voltage);
      if (pv && st.netLenByV[pv] != null) st.netLenByV[pv] += len;
    }

    const rows = (city || []).map(
      (c) =>
        statsByCity.get(String(c.id)) ?? {
          city: c,
          subsTotal: 0,
          subsByV: { ...base },
          netLenTotal: 0,
          netLenByV: { ...base },
        }
    );

    const getOrderIndex = (c) => {
      const slug = c?.slug ? String(c.slug) : "";
      if (CITY_ORDER_MAP.has(slug)) return CITY_ORDER_MAP.get(slug);
      return 10_000 + (c?.id ?? 0);
    };

    rows.sort((a, b) => getOrderIndex(a.city) - getOrderIndex(b.city));

    const total = rows.reduce(
      (acc, r) => {
        acc.subsTotal += r.subsTotal;
        acc.netLenTotal += r.netLenTotal;
        for (const v of VOLTS) {
          acc.subsByV[v] += r.subsByV[v] || 0;
          acc.netLenByV[v] += r.netLenByV[v] || 0;
        }
        return acc;
      },
      {
        city: { name: "Жами", slug: null },
        subsTotal: 0,
        subsByV: { ...base },
        netLenTotal: 0,
        netLenByV: { ...base },
      }
    );

    return { rows, total };
  }, [city, substations, networks]);

  const summaryLoading =
    loadingState.city || loadingState.substations || loadingState.networks;
  const summaryErrors = Object.values(errorState).filter(Boolean);
  const summaryMetrics = [
    {
      label: "Филиаллар",
      value: formatInt(summaryRows.rows.length),
      tone: "neutral",
    },
    {
      label: "Подстанциялар",
      value: formatInt(summaryRows.total.subsTotal),
      tone: "accent",
    },
    {
      label: "ЭУХТ, км",
      value: formatKm(summaryRows.total.netLenTotal),
      tone: "cyan",
    },
  ];

  return (
    <Layout>
      <div className="indicators">
        <div className="indicators-main">
          <div className="container">
            <section className="indicators-main__hero" aria-label="NES dashboard summary">
              <div>
                <p className="indicators-main__kicker">NES operational dashboard</p>
                <h1 className="indicators-main__title">
                  "Ўзбекистон Миллий электр тармоқлари" АЖ асосий кўрсатгичлари
                </h1>
                <p className="indicators-main__lead">
                  Филиаллар кесимида подстанциялар ва электр узатиш ҳаво тармоқлари
                  бўйича жамланган кўрсаткичлар.
                </p>
              </div>

              <div className="indicators-main__metrics" aria-label="Асосий жамланма">
                {summaryMetrics.map((metric) => (
                  <div
                    className={`indicators-main__metric indicators-main__metric--${metric.tone}`}
                    key={metric.label}
                  >
                    <span>{metric.label}</span>
                    <strong>{metric.value}</strong>
                  </div>
                ))}
              </div>
            </section>

            <div className="indicators-main__summary">
              <div className="indicators-main__summary-head">
                <div>
                  <div className="indicators-main__summary-title">
                    Филиаллар бўйича жадвал
                  </div>
                  <p className="indicators-main__summary-sub">
                    Ҳар бир филиал бўйича подстанциялар сони ва ЭУХТ узунлиги.
                  </p>
                </div>

                <div
                  className="indicators-summary-tabs"
                  role="tablist"
                  aria-label="Summary mode"
                >
                  <button
                    type="button"
                    className={`indicators-summary-tabs__btn ${
                      summaryMode === "substations" ? "is-active" : ""
                    }`}
                    onClick={() => setSummaryMode("substations")}
                    role="tab"
                    aria-selected={summaryMode === "substations"}
                  >
                    Подстанциялар
                  </button>
                  <button
                    type="button"
                    className={`indicators-summary-tabs__btn ${
                      summaryMode === "networks" ? "is-active" : ""
                    }`}
                    onClick={() => setSummaryMode("networks")}
                    role="tab"
                    aria-selected={summaryMode === "networks"}
                  >
                    ЭУХТ
                  </button>
                </div>
              </div>

              {summaryLoading && (
                <div className="indicators-main__notice indicators-main__notice--loading">
                  Маълумотлар юкланмоқда...
                </div>
              )}

              {summaryErrors.length > 0 && (
                <div className="indicators-main__notice indicators-main__notice--error">
                  {summaryErrors.join(" ")}
                </div>
              )}

              {!summaryLoading && summaryRows.rows.length === 0 && (
                <div className="indicators-main__notice indicators-main__notice--empty">
                  Кўрсатиш учун филиал маълумотлари топилмади.
                </div>
              )}

              <div className="indicators-main__summary-wrap">
                <div className="summary-desktop">
                  <table className="indicators-summary-table">
                    <thead>
                      <tr>
                        <th rowSpan="2" className="is-sticky-col">
                          Филиал
                        </th>
                        <th colSpan="5" className="group group--subs">
                          Подстанциялар
                        </th>
                        <th colSpan="5" className="group group--nets">
                          ЭУХТ
                        </th>
                      </tr>
                      <tr>
                        <th>Умумий</th>
                        <th>35</th>
                        <th>110</th>
                        <th>220</th>
                        <th>500</th>
                        <th>Умумий км.</th>
                        <th>35</th>
                        <th>110</th>
                        <th>220</th>
                        <th>500</th>
                      </tr>
                    </thead>

                    <tbody>
                      {summaryRows.rows.map((r) => (
                        <tr key={r.city?.id}>
                          <td className="is-sticky-col">
                            <Link
                              className="summary-branch-link"
                              to={`/indicators/${r.city.slug}`}
                            >
                              {`"${r.city.name}" филиали`}
                            </Link>
                          </td>

                          <td className="num">{formatInt(r.subsTotal)}</td>
                          <td className="num">{formatInt(r.subsByV["35"])}</td>
                          <td className="num">{formatInt(r.subsByV["110"])}</td>
                          <td className="num">{formatInt(r.subsByV["220"])}</td>
                          <td className="num">{formatInt(r.subsByV["500"])}</td>

                          <td className="num">{formatKm(r.netLenTotal)}</td>
                          <td className="num">{formatKm(r.netLenByV["35"])}</td>
                          <td className="num">{formatKm(r.netLenByV["110"])}</td>
                          <td className="num">{formatKm(r.netLenByV["220"])}</td>
                          <td className="num">{formatKm(r.netLenByV["500"])}</td>
                        </tr>
                      ))}

                      <tr className="is-total">
                        <td className="is-sticky-col total-label">
                          {summaryRows.total.city.name}
                        </td>
                        <td className="num">{formatInt(summaryRows.total.subsTotal)}</td>
                        <td className="num">{formatInt(summaryRows.total.subsByV["35"])}</td>
                        <td className="num">{formatInt(summaryRows.total.subsByV["110"])}</td>
                        <td className="num">{formatInt(summaryRows.total.subsByV["220"])}</td>
                        <td className="num">{formatInt(summaryRows.total.subsByV["500"])}</td>
                        <td className="num">{formatKm(summaryRows.total.netLenTotal)}</td>
                        <td className="num">{formatKm(summaryRows.total.netLenByV["35"])}</td>
                        <td className="num">{formatKm(summaryRows.total.netLenByV["110"])}</td>
                        <td className="num">{formatKm(summaryRows.total.netLenByV["220"])}</td>
                        <td className="num">{formatKm(summaryRows.total.netLenByV["500"])}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="summary-mobile">
                  <table className="indicators-summary-table indicators-summary-table--compact">
                    <thead>
                      <tr>
                        <th className="is-sticky-col">Филиал</th>
                        {SUMMARY_COLS[summaryMode].map((c) => (
                          <th key={c.key}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {summaryRows.rows.map((r) => (
                        <tr key={r.city?.id}>
                          <td className="is-sticky-col">
                            <Link
                              className="summary-branch-link"
                              to={`/indicators/${r.city.slug}`}
                            >
                              {`"${r.city.name}" филиали`}
                            </Link>
                          </td>

                          {SUMMARY_COLS[summaryMode].map((c) => (
                            <td key={c.key} className="num">
                              {renderSummaryCell(r, summaryMode, c.key)}
                            </td>
                          ))}
                        </tr>
                      ))}

                      <tr className="is-total">
                        <td className="is-sticky-col total-label">
                          {summaryRows.total.city.name}
                        </td>
                        {SUMMARY_COLS[summaryMode].map((c) => (
                          <td key={c.key} className="num">
                            {renderSummaryCell(summaryRows.total, summaryMode, c.key)}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Indicators;
